import { useState } from "react";
import { Link } from "react-router-dom";
import { BadgeEuro, CheckCircle2, Loader2, Send, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Marquee from "@/components/Marquee";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { api, errMsg } from "@/lib/api";
import { useShop } from "@/context/ShopContext";

const VALUES = [
  { icon: ShieldCheck, title: "Authenticité garantie", text: "Chaque produit provient de circuits officiels ou de déstockage vérifié. Nous ne vendons que du 100 % authentique, toujours." },
  { icon: BadgeEuro, title: "Prix transparents", text: "Le prix de référence est affiché, barré, et ton économie est calculée en euros. Pas de fausses promos, jamais." },
  { icon: Sparkles, title: "Sélection pointue", text: "Peu de produits, mais que des pépites : Huda Beauty, Rare Beauty, Kayali, Chanel… Les marques que tu aimes vraiment." },
];

export default function About() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { settings } = useShop() || {};

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="about-page">
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-16">
        <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-6">À propos</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl" data-testid="about-title">
          <MaskedLine delay={0.1}>Le luxe devient</MaskedLine>
          <MaskedLine delay={0.25} className="italic text-kay-pink">accessible.</MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-8 text-kay-ink/60 text-base md:text-lg leading-relaxed max-w-2xl">
            KAY BEAUTY est née d'une idée simple : les grandes marques de beauté ne devraient pas être un luxe
            réservé à quelques-unes. Nous dénichons des produits authentiques — maquillage, parfums, sacs —
            et nous les proposons jusqu'à -50 % du prix boutique. Tes marques préférées, à prix réduits.
          </p>
        </Reveal>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid md:grid-cols-3 gap-6">
        {VALUES.map((v, i) => (
          <Reveal key={v.title} delay={i * 0.1}>
            <div className="rounded-3xl border border-kay-line bg-white p-8 h-full shadow-[0_10px_30px_rgba(232,160,191,0.10)]">
              <span className="inline-flex w-12 h-12 rounded-full bg-kay-rose items-center justify-center mb-5">
                <v.icon className="text-kay-pink" size={20} />
              </span>
              <h3 className="font-display text-2xl mb-3">{v.title}</h3>
              <p className="text-sm text-kay-ink/60 leading-relaxed">{v.text}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-8">
        <Reveal>
          <div className="rounded-3xl bg-kay-ink text-kay-cream p-8 lg:p-14 grid lg:grid-cols-2 gap-10 items-center grain relative overflow-hidden">
            <div>
              <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-rosedeep mb-4">Commandes & paiement</p>
              <h2 className="font-display text-3xl sm:text-4xl leading-tight">Simple, direct, <span className="italic text-kay-rosedeep">humain.</span></h2>
              <p className="mt-5 text-white/60 text-sm leading-relaxed">
                Paie par PayPal ou virement bancaire. Une question sur ta commande, une photo à demander,
                une hésitation sur une teinte ? Écris-moi directement sur Snapchat.
              </p>
            </div>
            <div className="grid gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 px-6 py-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Paiement</p>
                <p className="mt-1 font-display text-xl">PayPal · Virement bancaire</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-6 py-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Contact direct</p>
                {settings?.snapchat && (
                  <p className="mt-1 font-display text-xl" data-testid="about-snapchat">Snapchat : {settings.snapchat}</p>
                )}
              </div>
              <Link to="/boutique" data-testid="about-cta" className="rounded-full bg-kay-rose text-kay-ink text-center py-4 text-sm font-bold hover:bg-white transition-colors">
                Découvrir les pépites
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <Reveal>
          <div className="rounded-3xl border border-kay-line bg-white p-8 lg:p-14 grid lg:grid-cols-2 gap-10 items-start shadow-[0_10px_30px_rgba(232,160,191,0.10)]">
            <div>
              <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-4">Une question ?</p>
              <h2 className="font-display text-3xl sm:text-4xl leading-tight">
                Écris-moi, <span className="italic text-kay-pink">je réponds vite.</span>
              </h2>
              <p className="mt-5 text-sm text-kay-ink/60 leading-relaxed">
                Une commande, une teinte, une demande spéciale ? Laisse-moi un message ici : il arrive directement
                dans ma boîte mail et tu reçois une confirmation immédiate.
              </p>
            </div>
            {sent ? (
              <div className="rounded-2xl bg-kay-rose/40 border border-kay-line p-8 text-center grid gap-3 justify-items-center" data-testid="contact-success">
                <CheckCircle2 className="text-kay-green" size={28} />
                <p className="font-display text-2xl">Message envoyé !</p>
                <p className="text-sm text-kay-ink/60">Un email de confirmation vient de partir. Je te réponds au plus vite.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="grid gap-3" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input required data-testid="contact-name" placeholder="Ton prénom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink" />
                  <input required type="email" data-testid="contact-email" placeholder="Ton email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink" />
                </div>
                <textarea required rows={4} data-testid="contact-message" placeholder="Ton message…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl border border-kay-line px-4 py-3 text-sm outline-none focus:border-kay-pink resize-none" />
                <button data-testid="contact-submit" disabled={sending} className="justify-self-start rounded-full bg-kay-ink text-white px-8 py-3.5 text-sm font-bold flex items-center gap-2 hover:bg-kay-pink transition-colors disabled:opacity-50">
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Envoyer mon message
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
